import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { LifeBuoy, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const CATEGORIES = [
  { value: "password_reset", label: "Password Reset" },
  { value: "credits", label: "Credits / Payments" },
  { value: "bug", label: "Bug Report" },
  { value: "feature", label: "Feature Request" },
  { value: "account", label: "Account Issue" },
  { value: "general", label: "General Question" },
];

export default function SupportModal({ trigger }) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  
  const [formData, setFormData] = useState({
    email: "",
    category: "general",
    subject: "",
    message: "",
  });

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        setFormData(prev => ({ ...prev, email: session.user.email || "" }));
        
        // Fetch profile for username
        const { data: profileData } = await supabase
          .from("profiles")
          .select("username")
          .eq("id", session.user.id)
          .single();
        
        if (profileData) {
          setProfile(profileData);
        }
      }
    };
    
    if (open) {
      fetchUser();
    }
  }, [open]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (!validateEmail(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.from("support_tickets").insert({
        user_id: user?.id || null,
        email: formData.email,
        username: profile?.username || null,
        category: formData.category,
        subject: formData.subject,
        message: formData.message,
      });
      
      if (error) throw error;
      
      toast.success("Support request submitted! We'll get back to you soon.");
      setOpen(false);
      setFormData({
        email: user?.email || "",
        category: "general",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting support ticket:", error);
      toast.error("Failed to submit support request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="ghost"
            className="text-xs text-[#808080] hover:text-[#a0a0a0] flex items-center gap-1.5"
          >
            <LifeBuoy className="w-4 h-4" />
            Support
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#0f0f0f] border-[#2a2a2a] text-[#e0e0e0] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#e0e0e0] flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-purple-400" />
            Contact Support
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-[#a0a0a0]">
              Email *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="your@email.com"
              className="bg-[#1a1a1a] border-[#2a2a2a] text-[#e0e0e0]"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="category" className="text-sm text-[#a0a0a0]">
              Category
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger className="bg-[#1a1a1a] border-[#2a2a2a] text-[#e0e0e0]">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value} className="text-[#e0e0e0]">
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-sm text-[#a0a0a0]">
              Subject *
            </Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Brief description of your issue"
              className="bg-[#1a1a1a] border-[#2a2a2a] text-[#e0e0e0]"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message" className="text-sm text-[#a0a0a0]">
              Message *
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Describe your issue in detail..."
              className="bg-[#1a1a1a] border-[#2a2a2a] text-[#e0e0e0] min-h-[120px]"
              required
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#2a2a2a] text-[#a0a0a0] hover:bg-[#1a1a1a]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}